/**
 * Server-side PostHog wrapper.
 * @package @app/analytics/server
 *
 * Lazy-singleton PostHog Node client. Use `track()` from server code (Stripe
 * webhook hooks, oRPC middleware, Inngest jobs). Pass `insertId` for any
 * event that may fire more than once for the same logical occurrence
 * (e.g. Stripe webhook retries) — PostHog dedupes by insert_id.
 *
 * No-op gracefully when POSTHOG_KEY is not configured (dev / local).
 *
 * IMPORTANT: posthog-node is dynamically imported. Top-level `import { PostHog }`
 * would pull the Node SDK (and its `node:fs` / `node:path` deps) into Vite's
 * client bundle through the SSR module graph, breaking the browser build.
 */

import type { PostHog as PostHogNode } from 'posthog-node';
import type { EventMap, EventName, UserProperties } from './events';

let clientSingleton: PostHogNode | null | undefined;

async function getClient(): Promise<PostHogNode | null> {
  if (clientSingleton !== undefined) return clientSingleton;

  const key = process.env.POSTHOG_KEY || process.env.VITE_POSTHOG_KEY;
  if (!key) {
    clientSingleton = null;
    return null;
  }

  const { PostHog } = await import('posthog-node');
  clientSingleton = new PostHog(key, {
    host: process.env.POSTHOG_HOST || process.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    // Default 20s flush interval is too slow for Stripe webhooks that complete
    // within ~200ms — events get buffered and lost when the function instance
    // is reused or torn down. flushAt:1 sends each event immediately.
    flushAt: 1,
    flushInterval: 0,
  });
  return clientSingleton;
}

interface TrackOptions<E extends EventName> {
  event: E;
  distinctId: string;
  properties: EventMap[E];
  /**
   * Deduplication key. PostHog drops duplicate events with the same insert_id
   * for a 24-hour window. Use for any event that may fire more than once for
   * the same logical occurrence (Stripe webhook retries pass the same event.id).
   */
  insertId?: string;
}

/**
 * Capture an event server-side. Non-throwing — analytics failures must never
 * affect business logic. Logs to console on error so failures are visible.
 */
export async function track<E extends EventName>(opts: TrackOptions<E>): Promise<void> {
  try {
    const client = await getClient();
    if (!client) return;

    const properties: Record<string, unknown> = { ...opts.properties };
    if (opts.insertId) properties.$insert_id = opts.insertId;

    client.capture({
      event: opts.event,
      distinctId: opts.distinctId,
      properties,
    });
    // flushAt:1 makes capture() async-fire; await ensures network egress
    // completes before the serverless function is frozen.
    await client.flush();
  } catch (err) {
    console.error('[analytics] track failed:', err);
  }
}

/**
 * Server-side identify — sets person properties without emitting an event.
 * Use sparingly from server (the client already identifies on auth); useful
 * for properties only the server knows (e.g. plan changes from webhooks).
 */
export async function identify(
  distinctId: string,
  properties: Partial<UserProperties>,
): Promise<void> {
  try {
    const client = await getClient();
    if (!client) return;
    client.identify({ distinctId, properties });
    await client.flush();
  } catch (err) {
    console.error('[analytics] identify failed:', err);
  }
}

/**
 * Shut down the client cleanly. Call from graceful-shutdown handlers.
 */
export async function shutdown(): Promise<void> {
  if (clientSingleton) {
    await clientSingleton.shutdown();
    clientSingleton = undefined;
  }
}
